// @flow
import { isAfter, compareDesc } from "date-fns"
import { merge, prepend, evolve, remove, indexBy, pipe, filter } from "ramda"

import set from "common/utils/set"
import { STARRED } from "common/consts/localStorageKeys"
import { setItem } from "common/browser/utils/localStorage"
import Journey from "common/records/Journey"
import cuckoo from "common/services/cuckoo/cuckoo"
import flightTrackingData from "common/services/tracking/flightTrackingData"
import { DEFAULT_CABIN_CLASS, DEFAULT_CABIN_CLASS_TYPE } from "common/records/CabinClass"
import PassengersCount from "common/records/PassengersCount"
import type Place from "common/records/Place"
import type { CabinClass } from "common/records/CabinClass"
import type { StarredFormData, StarredItem, StarredStore } from "./Starred.types"

const _trackStarredFlights = (store: StarredStore): StarredStore => {
  cuckoo.infinario(
    "starredFlights",
    store.items.map(i => flightTrackingData(i.journey, i.form.passengers)),
  )

  return store
}

export const getItemId = (
  journeyOrId: Journey | string,
  passengers: PassengersCount,
  cabinClass: ?CabinClass,
): string =>
  [
    typeof journeyOrId === "string" ? journeyOrId : journeyOrId.getId(),
    `adults:${passengers.adults}`,
    `infants:${passengers.infants}`,
    passengers.children > 0 ? `children:${passengers.children}` : null,
    cabinClass && cabinClass.type && cabinClass.type !== DEFAULT_CABIN_CLASS_TYPE
      ? `cabinClass:${cabinClass.type}-${cabinClass.allowMixed.toString()}`
      : null,
  ]
    .filter(Boolean)
    .join("-")

const createItem = (journey: Journey, form: StarredFormData): StarredItem => {
  const now = new Date()

  return {
    id: getItemId(journey, form.passengers, form.cabinClass),
    form,
    journey: journey.set("_original", null),
    lastPrice: journey.getBestPrice(),
    priceUpdatedAt: null,
    createdAt: now,
    updatedAt: now,
  }
}

export const itemIsValid = (item: StarredItem): boolean =>
  isAfter(item.journey.getDeparture().getIn(["when", "utc"]), Date.now())

export const itemPriceChanged = (item: StarredItem): boolean =>
  item.lastPrice !== item.journey.getBestPrice()

export const itemUpdate = (item: StarredItem, journey: Journey): StarredItem =>
  item.journey.equals(journey)
    ? item
    : merge(item, {
        journey,
        updatedAt: new Date(),
        priceUpdatedAt: itemPriceChanged(item) ? new Date() : item.priceUpdatedAt,
      })

export const itemSetSeen = (item: StarredItem): StarredItem =>
  set("lastPrice", item.journey.getBestPrice(), item)

export const itemFromPlainJS = (input: any): StarredItem => ({
  ...input,
  form: {
    ...input.form,
    passengers: new PassengersCount(input.form.passengers),
    cabinClass: input.form.cabinClass || DEFAULT_CABIN_CLASS,
  },
  journey: Journey.fromPlainJS(input.journey),
  priceUpdatedAt: input.priceUpdatedAt && new Date(input.priceUpdatedAt),
  createdAt: new Date(input.createdAt),
  updatedAt: new Date(input.updatedAt),
})

export const storeFindItem = (
  store: StarredStore,
  journeyOrId: Journey | string,
  passengers: PassengersCount,
  cabinClass: ?CabinClass,
): ?StarredItem => {
  const searchId = getItemId(journeyOrId, passengers, cabinClass)

  return store.items.find(item => item.id === searchId)
}

export const storeAddItem = (
  store: StarredStore,
  journey: Journey,
  form: StarredFormData,
): StarredStore => {
  const newItemId = getItemId(journey, form.passengers, form.cabinClass)

  return _trackStarredFlights(
    evolve(
      {
        items: pipe(
          filter(item => item.id !== newItemId),
          prepend(createItem(journey, form)),
        ),
      },
      store,
    ),
  )
}

export const storeRemoveItem = (store: StarredStore, id: string): StarredStore => {
  cuckoo.infinario("starFlight", { action: "starRemove" })

  const index = store.items.findIndex(item => item.id === id)

  return _trackStarredFlights(index < 0 ? store : evolve({ items: remove(index, 1) }, store))
}

export const storeUpdateItems = (
  store: StarredStore,
  journeys: Array<Journey>,
  passengers: PassengersCount,
  cabinClass: CabinClass,
): StarredStore => {
  const idToJourney = indexBy(journey => getItemId(journey, passengers, cabinClass), journeys)

  return evolve(
    {
      items: items =>
        items.map(item => (idToJourney[item.id] ? itemUpdate(item, idToJourney[item.id]) : item)),
    },
    store,
  )
}

export const storeSetSeenItems = (store: StarredStore, ids: ?Array<string>) =>
  evolve(
    {
      items: items => items.map(item => (!ids || ids.includes(item.id) ? itemSetSeen(item) : item)),
    },
    store,
  )

export const storeSetPlaces = (store: StarredStore, newPlaces: Array<Place>) =>
  evolve(
    {
      places: places => merge(places, indexBy(place => place.id, newPlaces)),
    },
    store,
  )

export const storeFromPlainJS = (input: any): StarredStore => ({
  items: input.items.map(itemFromPlainJS).sort((a, b) => compareDesc(a.createdAt, b.createdAt)),
  places: {},
})

export const storePersist = (store: StarredStore): void => {
  setItem(STARRED, JSON.stringify(store.items))
}
